/**
 * GameDayTab.jsx — Game Day: scalping a custom stock ticker
 *
 * For active stock scalping (debit spreads, starting at market open).
 * Core features:
 *   1. Pre-market direction signal — always visible, tells you which way
 *      to open your trade right at 9:30am to capture maximum premium.
 *   2. Opening countdown / "ENTER NOW" flash at market open.
 *   3. Debit spread position tracker — you bought, not sold.
 *      Monitors P&L and alerts when to sell based on indicators.
 *   4. Same configurable indicators as Safe Day (MACD, Stochastic, RSI, etc.)
 *   5. Configurable ticker via settings.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { playBeep, playAudio, DEFAULT_GAME_DAY_ALERT } from '../../hooks/useAlertEngine';
import { tierGetRingtone, tierPutRingtone, tierDeleteRingtone, BASE, getToken } from '../../lib/api';
import { analyzePreMarket } from '../../lib/sessionRegime';
import {
  aggregateBars, calcEMA, calcEMASeries, calcMACD, calcMACross,
  calcStochastic, calcRSI, minsRequired, estimateDebitPnl,
} from '../../lib/indicators';
import {
  StrategyA, StrategyB, CombinedSignal, Panel, Metric,
  PositionForm, MACDPanel, MACrossPanel, ExitSignalSummary, calcExitSignals,
  GEXPanel, ZeroDTEFlowPanel, OptionsChainPicker,
} from './SafeDayTab';

const GAME_RINGTONE_ID = 'gameday';

// ── Pre-market direction algorithm ────────────────────────────────────────────

function getOpeningDirection(pm, vixPrice, orbDir = null, emaDir = null) {
  const vixNote = vixPrice != null
    ? vixPrice >= 18
      ? `VIX ${vixPrice.toFixed(1)} elevated — bigger moves expected. Good for debit spread buyers.`
      : `VIX ${vixPrice.toFixed(1)} low — small moves expected. Weigh whether the premium you pay is worth it.`
    : '';

  // ── Live bar signals take priority over pre-market data ─────────────────
  const liveDir = (orbDir === 'up' || emaDir === 'up') ? 'BULL'
                : (orbDir === 'down' || emaDir === 'down') ? 'BEAR' : null;
  if (liveDir) {
    const bothAgree = orbDir && emaDir && orbDir === emaDir;
    const sources   = [orbDir && 'ORB breakout', emaDir && 'VWAP/EMA'].filter(Boolean).join(' + ');
    return {
      dir: liveDir, confidence: bothAgree ? 90 : 70, vixNote,
      label: liveDir === 'BULL' ? '↑ Buy Call Spread — live trend confirmed' : '↓ Buy Put Spread — live trend confirmed',
      color: liveDir === 'BULL' ? '#22c55e' : '#ef4444',
      notes: [
        `Live signal from ${sources} — ${liveDir === 'BULL' ? 'uptrend' : 'downtrend'} confirmed by price action.`,
        bothAgree ? 'Both ORB and VWAP/EMA agree — high conviction. Full sizing is appropriate.' : 'One strategy confirmed. Valid setup — trade at 50–75% size and wait for second confirmation.',
      ].filter(Boolean),
    };
  }

  if (!pm?.ready) return { dir: 'LOADING', color: 'var(--faint)', confidence: 0, notes: [], vixNote: '' };

  const signals = [];

  // Coil — strongest override
  if (pm.coil?.coiling) return {
    dir: 'WAIT',
    label: 'Wait for breakout',
    color: '#ef4444',
    confidence: 0,
    notes: [
      `Stuck in a range ($${pm.coil.bandLow}–$${pm.coil.bandHigh}) for ${pm.coil.daysInBand} days.`,
      `Don't buy before the break. Wait for a clean move above $${pm.coil.bandHigh} or below $${pm.coil.bandLow}, then buy in that direction.`,
    ],
    vixNote: '',
  };

  // Gap signals
  if (pm.gapPct != null) {
    const absGap = Math.abs(pm.gapPct);
    if (absGap >= 1.0)
      signals.push({ weight: 12, dir: pm.gapPct > 0 ? 'BULL' : 'BEAR',
        note: `Big gap ${pm.gapPct > 0 ? 'up' : 'down'} ${absGap.toFixed(2)}% overnight — strongest directional signal.` });
    else if (absGap >= 0.5)
      signals.push({ weight: 6, dir: pm.gapPct > 0 ? 'BULL' : 'BEAR',
        note: `Moderate gap ${pm.gapPct > 0 ? 'up' : 'down'} ${absGap.toFixed(2)}%.` });
    else if (absGap >= 0.3)
      signals.push({ weight: 3, dir: pm.gapPct > 0 ? 'BULL' : 'BEAR',
        note: `Small gap ${pm.gapPct > 0 ? 'up' : 'down'} ${absGap.toFixed(2)}% — mild signal.` });
  }

  // Daily trend direction
  if (pm.dailyADX != null && pm.dailyADX >= 20 && pm.priorDir && pm.priorDir !== 'flat') {
    const dir = pm.priorDir === 'up' ? 'BULL' : 'BEAR';
    signals.push({ weight: pm.dailyADX >= 25 ? 7 : 4, dir,
      note: `Daily trend is ${pm.priorDir === 'up' ? 'upward' : 'downward'} (ADX ${pm.dailyADX?.toFixed(0)}).` });
  }

  // Near prior day level — directional
  if (pm.distToHighPct != null && pm.distToHighPct < 0.3 && pm.gapPct != null && pm.gapPct > 0)
    signals.push({ weight: 4, dir: 'BULL', note: `Price gapping toward yesterday's high ($${pm.priorHigh}) — potential breakout.` });
  if (pm.distToLowPct  != null && pm.distToLowPct  < 0.3 && pm.gapPct != null && pm.gapPct < 0)
    signals.push({ weight: 4, dir: 'BEAR', note: `Price gapping toward yesterday's low ($${pm.priorLow}) — potential breakdown.` });

  const bullScore = signals.filter(s => s.dir === 'BULL').reduce((s, x) => s + x.weight, 0);
  const bearScore = signals.filter(s => s.dir === 'BEAR').reduce((s, x) => s + x.weight, 0);
  const total     = bullScore + bearScore || 1;

  const vixGood = vixPrice != null && vixPrice >= 18;

  if (bullScore > bearScore * 1.4) return {
    dir: 'BULL', label: '↑ Buy Call Spread at Open', color: '#22c55e',
    confidence: Math.round(bullScore / total * 100),
    notes: signals.filter(s => s.dir === 'BULL').map(s => s.note),
    vixNote,
  };
  if (bearScore > bullScore * 1.4) return {
    dir: 'BEAR', label: '↓ Buy Put Spread at Open', color: '#ef4444',
    confidence: Math.round(bearScore / total * 100),
    notes: signals.filter(s => s.dir === 'BEAR').map(s => s.note),
    vixNote,
  };
  if (bullScore === 0 && bearScore === 0) return {
    dir: 'UNCLEAR', label: 'No clear direction — wait', color: '#f59e0b',
    confidence: 0, notes: ['No strong pre-market signals. Let the first 5 minutes of trading tell you the direction before buying.'], vixNote,
  };
  return {
    dir: 'MIXED', label: 'Mixed signals — wait for open', color: '#f59e0b',
    confidence: 0,
    notes: [...signals.filter(s => s.dir==='BULL').map(s=>s.note), ...signals.filter(s=>s.dir==='BEAR').map(s=>s.note)],
    vixNote,
  };
}

// ── ET time helper ────────────────────────────────────────────────────────────
function etNow() {
  const str = new Date().toLocaleTimeString('en-US', { timeZone:'America/New_York', hour12:false, hour:'2-digit', minute:'2-digit', second:'2-digit' });
  const [h, m, s] = str.split(':').map(Number);
  return { h, m, s, totalSecs: h*3600 + m*60 + s };
}
const OPEN_SECS  = 9*3600 + 30*60; // 9:30 ET
const CLOSE_SECS = 16*3600;         // 4:00 ET

// ── Exit signals for long debit spread ────────────────────────────────────────
function calcGameExitSignals(pos, price, allInds, alertCfg) {
  const signals = [];
  if (!pos.active || !price) return signals;
  const isBull = pos.type === 'call_debit';
  const cfg    = alertCfg?.indicators ?? {};

  // P&L based exits
  const debitPnl = estimateDebitPnl(pos, price);
  if (debitPnl) {
    if (debitPnl.pnl < -debitPnl.maxLoss * 0.5)
      signals.push({ level:'STOP LOSS', color:'#ef4444',
        text:`Position down ~50% of what you paid ($${Math.abs(debitPnl.pnl).toFixed(0)}). Cut the loss now. The move didn't happen — exit and wait for the next setup.` });
    else if (debitPnl.pctOfMax >= 75)
      signals.push({ level:'TAKE PROFIT', color:'#22c55e',
        text:`Up ~${debitPnl.pctOfMax.toFixed(0)}% of max profit (+$${debitPnl.pnl.toFixed(0)}). Excellent gain — sell now. Don't hold for the last 25%.` });
    else if (debitPnl.pctOfMax >= 50)
      signals.push({ level:'TAKE PROFIT', color:'#22c55e',
        text:`At ~${debitPnl.pctOfMax.toFixed(0)}% of max profit (+$${debitPnl.pnl.toFixed(0)}). This is the 50% target — sell and lock in the win.` });
    else if (debitPnl.pctOfMax >= 30)
      signals.push({ level:'CAUTION', color:'#f59e0b',
        text:`At ~${debitPnl.pctOfMax.toFixed(0)}% of max profit. Watch indicators — sell if momentum turns against you.` });
  }

  const { macd, maCross, stoch, rsi: rsiVal, vwapCrossed } = allInds;

  // MACD against position
  if (cfg.macd?.enabled && macd) {
    if (isBull && macd.bearishCross) signals.push({ level:'EXIT SIGNAL', color:'#f59e0b',
      text:`MACD turned bearish (${cfg.macd.timeframeMins}-min) — upward momentum is fading. Sell your call spread before the move reverses.` });
    if (!isBull && macd.bullishCross) signals.push({ level:'EXIT SIGNAL', color:'#f59e0b',
      text:`MACD turned bullish (${cfg.macd.timeframeMins}-min) — downward momentum fading. Sell your put spread.` });
  }

  // MA crossover
  if (cfg.maCross?.enabled && maCross) {
    if (isBull && maCross.bearishCross) signals.push({ level:'EXIT SIGNAL', color:'#f97316',
      text:`Short-term trend flipped bearish (${cfg.maCross.fastPeriod}/${cfg.maCross.slowPeriod} EMA cross). Close the call spread — the trend has turned against you.` });
    if (!isBull && maCross.bullishCross) signals.push({ level:'EXIT SIGNAL', color:'#f97316',
      text:`Short-term trend flipped bullish. Close the put spread.` });
    // Momentum confirmation
    if (isBull && maCross.bullish && !maCross.bullishCross) signals.push({ level:'MOMENTUM', color:'#818cf8',
      text:`Trend still bullish — your call spread has support. Hold if within your time limit.` });
    if (!isBull && !maCross.bullish && !maCross.bearishCross) signals.push({ level:'MOMENTUM', color:'#818cf8',
      text:`Trend still bearish — your put spread has support.` });
  }

  // Stochastic
  if (cfg.stochastic?.enabled && stoch) {
    const ob=cfg.stochastic.overbought, os=cfg.stochastic.oversold;
    if (isBull && stoch.bearishCross && stoch.k > ob)
      signals.push({ level:'EXIT SIGNAL', color:'#f59e0b', text:`Stochastic %K overbought (${stoch.k.toFixed(0)}) and rolling over. The upward move may be exhausted — sell.` });
    if (!isBull && stoch.bullishCross && stoch.k < os)
      signals.push({ level:'EXIT SIGNAL', color:'#f59e0b', text:`Stochastic %K oversold (${stoch.k.toFixed(0)}) and turning up. Downward move may be exhausted — sell.` });
  }

  // RSI
  if (cfg.rsiExit?.enabled && rsiVal != null) {
    const nz = cfg.rsiExit.neutralZone ?? 50;
    if (isBull && rsiVal < nz) signals.push({ level:'CAUTION', color:'#f59e0b',
      text:`RSI ${rsiVal.toFixed(1)} below ${nz} — bullish momentum weakening. Watch closely.` });
    if (!isBull && rsiVal > (100-nz)) signals.push({ level:'CAUTION', color:'#f59e0b',
      text:`RSI ${rsiVal.toFixed(1)} above ${100-nz} — bearish momentum weakening.` });
  }

  // VWAP cross against position
  if (cfg.vwapCross?.enabled && vwapCrossed != null) {
    if (isBull && vwapCrossed === 'below') signals.push({ level:'EXIT SIGNAL', color:'#f97316',
      text:`Price crossed below the average price (VWAP) — institutional sellers took over. Exit the call spread immediately.` });
    if (!isBull && vwapCrossed === 'above') signals.push({ level:'EXIT SIGNAL', color:'#f97316',
      text:`Price crossed above VWAP — buyers taking over. Exit the put spread.` });
  }

  const seen = new Set();
  return signals.filter(s=>{ const k=s.level+s.text.slice(0,30); if(seen.has(k))return false; seen.add(k); return true; });
}

// ── Sub-components ────────────────────────────────────────────────────────────

// Direction panel — always at top
// ── News Headlines Panel ──────────────────────────────────────────────────────
function NewsPanel({ news, loading, ticker }) {
  if (loading) return (
    <div style={{ padding:'10px 14px', borderRadius:8, border:'1px solid var(--border)',
      background:'var(--bg)', marginBottom:12, fontSize:11, color:'var(--faint)' }}>
      ⟳ Loading {ticker} headlines…
    </div>
  );
  if (!news) return null;

  const biasColor = news.bias==='BULLISH'?'#22c55e':news.bias==='BEARISH'?'#ef4444':'#f59e0b';
  const biasIcon  = news.bias==='BULLISH'?'↑':news.bias==='BEARISH'?'↓':'→';
  const age = news.fetchedAt ? Math.round((Date.now()-news.fetchedAt)/60000) : null;

  return (
    <div style={{ border:`1px solid ${biasColor}50`, borderRadius:8, padding:'12px 14px',
      marginBottom:12, background:biasColor+'08' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:6 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:9, fontWeight:800, letterSpacing:'.1em', color:'var(--faint)', textTransform:'uppercase' }}>
            {ticker} · News Sentiment
          </span>
          <span style={{ fontSize:12, fontWeight:900, color:biasColor }}>{biasIcon} {news.bias}</span>
        </div>
        {age!=null && <span style={{ fontSize:9, color:'var(--faint)', fontFamily:'monospace' }}>
          {age<2?'just fetched':`${age}min ago`} · cached 1h
        </span>}
      </div>
      {news.summary && (
        <div style={{ fontSize:11, color:'var(--text)', lineHeight:1.6, marginBottom:10,
          padding:'7px 10px', borderRadius:5, background:'var(--bg)', border:'1px solid var(--border)' }}>
          {news.summary}
        </div>
      )}
      <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
        {(news.headlines??[]).map((h,i)=>(
          <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
            <span style={{ fontSize:9, color:'var(--faint)', flexShrink:0, marginTop:2, fontFamily:'monospace' }}>{i+1}.</span>
            <div style={{ flex:1 }}>
              <a href={h.link} target="_blank" rel="noopener noreferrer"
                style={{ fontSize:11, color:'var(--text)', textDecoration:'none', lineHeight:1.45, display:'block' }}
                onMouseEnter={e=>e.target.style.textDecoration='underline'}
                onMouseLeave={e=>e.target.style.textDecoration='none'}>
                {h.title}
              </a>
              {h.publisher && <span style={{ fontSize:9, color:'var(--faint)' }}>
                {h.publisher}{h.time?` · ${new Date(h.time).toLocaleDateString()}`:''}
              </span>}
            </div>
          </div>
        ))}
        {!news.headlines?.length && <span style={{ fontSize:11, color:'var(--faint)', fontStyle:'italic' }}>No headlines found.</span>}
      </div>
    </div>
  );
}

function DirectionPanel({ direction, marketOpen, minutesIn, ticker }) {
  const [countdown, setCountdown] = useState('');
  useEffect(() => {
    const tick = () => {
      const { totalSecs } = etNow();
      if (!marketOpen) {
        const diff = OPEN_SECS - totalSecs;
        if (diff > 0) {
          const m = Math.floor(diff/60), s = diff%60;
          setCountdown(`Market opens in ${m}m ${s}s`);
        } else setCountdown('');
      } else {
        setCountdown('');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [marketOpen]);

  const isActionable = direction.dir === 'BULL' || direction.dir === 'BEAR';
  const justOpened   = marketOpen && minutesIn < 5;

  return (
    <div style={{ border:`2px solid ${direction.color}`, borderRadius:10, padding:'14px 16px', marginBottom:12,
      background: direction.color + '10', position:'relative', overflow:'hidden' }}>
      {/* "ENTER NOW" flash at open */}
      {justOpened && isActionable && (
        <div style={{ position:'absolute', top:8, right:12, fontSize:9, fontWeight:800,
          letterSpacing:'.12em', color:'var(--bg)', background:direction.color,
          padding:'2px 8px', borderRadius:3, animation:'alertPulse 1s infinite' }}>
          ⚡ ENTER NOW
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <div>
          <div style={{ fontSize:9, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--faint)', marginBottom:4 }}>
            {ticker} · Opening Direction
          </div>
          <div style={{ fontSize:22, fontWeight:900, color:direction.color, lineHeight:1.1 }}>
            {direction.label ?? direction.dir}
          </div>
          {direction.confidence > 0 && (
            <div style={{ fontSize:10, color:'var(--faint)', marginTop:3 }}>
              Signal confidence: {direction.confidence}%
            </div>
          )}
        </div>
        {countdown && (
          <div style={{ fontSize:11, fontFamily:'monospace', color:'var(--warn)', textAlign:'right' }}>
            {countdown}
          </div>
        )}
      </div>

      {/* Notes */}
      <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom: direction.vixNote ? 8 : 0 }}>
        {direction.notes.map((n,i) => (
          <div key={i} style={{ fontSize:11, color:'var(--text)', lineHeight:1.5 }}>
            <span style={{ color:direction.color, marginRight:6 }}>{i===0?'→':'·'}</span>{n}
          </div>
        ))}
      </div>

      {direction.vixNote && (
        <div style={{ fontSize:10, color:'var(--muted)', lineHeight:1.5, borderTop:'1px solid var(--border)', paddingTop:8, marginTop:4 }}>
          {direction.vixNote}
        </div>
      )}

      {/* Strategy card */}
      {isActionable && (
        <div style={{ marginTop:10, padding:'8px 12px', borderRadius:6, background:'var(--bg)',
          border:`1px solid ${direction.color}40` }}>
          <div style={{ fontSize:10, fontWeight:700, color:direction.color, marginBottom:3 }}>
            Recommended trade at open
          </div>
          <div style={{ fontSize:11, color:'var(--text)', lineHeight:1.55 }}>
            {direction.dir === 'BULL'
              ? `Buy a call debit spread — buy a call at or just below today's opening price, sell a call $2–$5 higher. This costs you a fixed amount and profits if ${ticker} moves up.`
              : `Buy a put debit spread — buy a put at or just above today's opening price, sell a put $2–$5 lower. This costs you a fixed amount and profits if ${ticker} moves down.`}
          </div>
          {!marketOpen && (
            <div style={{ fontSize:10, color:'var(--faint)', marginTop:4, fontStyle:'italic' }}>
              Wait for the bell. Once price confirms the direction (stays above opening price for 2+ candles), enter.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Debit position tracker
function DebitPositionForm({ pos, onChange, direction, ticker }) {
  const isBull   = pos.type === 'call_debit';
  const debitPnl = pos.active ? estimateDebitPnl(pos, null) : null; // price-free, just show structure
  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'14px 16px', marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <span style={{ fontSize:11, fontWeight:800, letterSpacing:'.05em', color:'var(--text)' }}>
          My {ticker} Position
        </span>
        <div style={{ display:'flex', gap:6 }}>
          {!pos.active && direction.dir === 'BULL' && (
            <button onClick={()=>onChange({...pos,type:'call_debit',active:false})}
              style={{ fontSize:9, padding:'2px 8px', border:'1px solid #22c55e', borderRadius:3,
                background:'transparent', color:'#22c55e', cursor:'pointer' }}>
              ↑ Set to Buy Bull Call Spread
            </button>
          )}
          {!pos.active && direction.dir === 'BEAR' && (
            <button onClick={()=>onChange({...pos,type:'put_debit',active:false})}
              style={{ fontSize:9, padding:'2px 8px', border:'1px solid #ef4444', borderRadius:3,
                background:'transparent', color:'#ef4444', cursor:'pointer' }}>
              ↓ Set to Buy Bear Put Spread
            </button>
          )}
          {pos.active && (
            <button onClick={()=>onChange({...pos,active:false})}
              style={{ fontSize:9, padding:'2px 7px', border:'1px solid var(--border)', borderRadius:3,
                background:'transparent', color:'var(--faint)', cursor:'pointer' }}>✕ Clear</button>
          )}
        </div>
      </div>

      {!pos.active ? (
        <div style={{ display:'grid', gap:10 }}>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <div>
              <div style={fL}>Spread type</div>
              <select value={pos.type} onChange={e=>onChange({...pos,type:e.target.value})} style={fI}>
                <option value="">Select…</option>
                <option value="call_debit">Buy Bull Call Spread</option>
                <option value="put_debit">Buy Bear Put Spread</option>
              </select>
            </div>
            <div>
              <div style={fL}>Long strike (buy)</div>
              <input type="number" placeholder="e.g. 220" value={pos.longStrike}
                onChange={e=>onChange({...pos,longStrike:e.target.value})} style={{...fI,width:90}}/>
            </div>
            <div>
              <div style={fL}>Short strike (sell)</div>
              <input type="number" placeholder="e.g. 225" value={pos.shortStrike}
                onChange={e=>onChange({...pos,shortStrike:e.target.value})} style={{...fI,width:90}}/>
            </div>
            <div>
              <div style={fL}>Premium paid ($)</div>
              <input type="number" step="0.01" placeholder="e.g. 2.50" value={pos.premium}
                onChange={e=>onChange({...pos,premium:e.target.value})} style={{...fI,width:90}}/>
            </div>
            <div>
              <div style={fL}>Contracts</div>
              <input type="number" min={1} placeholder="1" value={pos.contracts}
                onChange={e=>onChange({...pos,contracts:e.target.value})} style={{...fI,width:70}}/>
            </div>
          </div>
          <div style={{ fontSize:10, color:'var(--faint)', lineHeight:1.5 }}>
            Enter the spread you bought. The dashboard will estimate your current P&L based on {ticker}'s price and alert you when to sell.
          </div>
          <button
            disabled={!pos.type||!pos.longStrike||!pos.shortStrike||!pos.premium}
            onClick={()=>onChange({...pos,active:true})}
            style={{ alignSelf:'flex-start', padding:'8px 18px', borderRadius:5, border:'none',
              background:pos.type&&pos.longStrike&&pos.premium?'var(--accent)':'var(--border)',
              color:pos.type&&pos.longStrike&&pos.premium?'var(--bg)':'var(--faint)',
              fontWeight:700, fontSize:12, cursor:'pointer' }}>
            Track this position
          </button>
        </div>
      ) : (
        <div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:20, marginBottom:10 }}>
            <Metric label="Spread" color={isBull?'var(--up)':'var(--down)'}
              value={isBull?'Buy Bull Call':'Buy Bear Put'}
              sub={`$${pos.longStrike} / $${pos.shortStrike}`}/>
            <Metric label="Paid" color="var(--down)"
              value={`$${Number(pos.premium).toFixed(2)}`} sub="per share / contract"/>
            <Metric label="Max profit" color="var(--up)"
              value={`$${((Math.abs(Number(pos.shortStrike)-Number(pos.longStrike))-Number(pos.premium))*100*Number(pos.contracts)).toFixed(0)}`}
              sub="if spread fully in-the-money"/>
            <Metric label="Max loss" color="var(--down)"
              value={`$${(Number(pos.premium)*100*Number(pos.contracts)).toFixed(0)}`}
              sub="if spread expires worthless"/>
            <Metric label="50% target" color="var(--warn)"
              value={`$${((Math.abs(Number(pos.shortStrike)-Number(pos.longStrike))-Number(pos.premium))*100*Number(pos.contracts)*0.5).toFixed(0)}`}
              sub="sell here"/>
            <Metric label="Stop loss" color="var(--down)"
              value={`$${(Number(pos.premium)*100*Number(pos.contracts)*0.5).toFixed(0)}`}
              sub="exit if down this much"/>
          </div>
          <div style={{ fontSize:10, color:'var(--faint)', lineHeight:1.5, padding:'6px 10px',
            borderRadius:5, background:'var(--bg-elev,var(--bg))', border:'1px solid var(--border)' }}>
            Breakeven: ${pos.type === 'call_debit'
              ? (Number(pos.longStrike)+Number(pos.premium)).toFixed(2)
              : (Number(pos.longStrike)-Number(pos.premium)).toFixed(2)} at expiry.
            P&L estimate below is approximate — check live option prices for accuracy.
          </div>
        </div>
      )}
    </div>
  );
}

// Live P&L bar
function PnlBar({ pos, currentPrice }) {
  if (!pos.active || !currentPrice) return null;
  const d = estimateDebitPnl(pos, currentPrice);
  if (!d) return null;
  const pct   = Math.max(-100, Math.min(100, d.pctOfMax));
  const color  = d.pnl >= 0 ? 'var(--up)' : 'var(--down)';
  return (
    <div style={{ padding:'10px 14px', borderRadius:8, border:`1px solid ${color}40`,
      background: color + '0c', marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
        <span style={{ fontSize:11, color:'var(--muted)' }}>Estimated P&L</span>
        <span style={{ fontSize:16, fontWeight:900, fontFamily:'monospace', color }}>
          {d.pnl >= 0 ? '+' : ''}{d.pnl.toFixed(0)}
          <span style={{ fontSize:10, fontWeight:400, marginLeft:6, color:'var(--faint)' }}>
            ({d.pctOfMax.toFixed(0)}% of max)
          </span>
        </span>
      </div>
      <div style={{ height:8, background:'var(--border)', borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:`${Math.max(0,pct)}%`, height:'100%', background:color,
          borderRadius:4, transition:'width .5s', minWidth: d.pnl < 0 ? 0 : 4 }} />
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:9, color:'var(--faint)', marginTop:3 }}>
        <span>-${d.maxLoss} (max loss)</span>
        <span>+${d.maxProfit} (max profit)</span>
      </div>
    </div>
  );
}

// Exit signal display
function ExitSignals({ signals, pos }) {
  if (!pos.active) return null;
  if (!signals.length) return (
    <div style={{ padding:'10px 14px', borderRadius:8, border:'1px solid var(--up)', background:'#22c55e0e', marginBottom:12 }}>
      <span style={{ fontSize:12, fontWeight:700, color:'var(--up)' }}>✓ Hold</span>
      <span style={{ fontSize:11, color:'var(--muted)', marginLeft:10 }}>No exit signals. Position looks fine.</span>
    </div>
  );
  return (
    <div style={{ marginBottom:12 }}>
      {signals.map((sig,i) => (
        <div key={i} style={{ padding:'10px 14px', borderRadius:8, marginBottom:6,
          border:`1px solid ${sig.color}`, background:`${sig.color}12`,
          display:'flex', alignItems:'flex-start', gap:10 }}>
          <span style={{ fontSize:9, fontWeight:800, padding:'2px 7px', borderRadius:3,
            background:sig.color, color:'var(--bg)', flexShrink:0, marginTop:1, letterSpacing:'.06em' }}>
            {sig.level}
          </span>
          <span style={{ fontSize:11, color:'var(--text)', lineHeight:1.55 }}>{sig.text}</span>
        </div>
      ))}
    </div>
  );
}

// Indicator panels (compact)
function IndicatorStrip({ macd, maCross, stoch, rsi: rsiVal, cfg, minutesIn }) {
  const items = [
    macd && { label:`MACD ${cfg?.macd?.timeframeMins??1}m`, value: macd.histogram.toFixed(3),
      sub: macd.bearishCross?'▼ bearish cross':macd.bullishCross?'▲ bullish cross':macd.histogram>=0?'bullish':'bearish',
      color: macd.histogram>=0?'var(--up)':'var(--down)' },
    maCross && { label:`EMA ${cfg?.maCross?.fastPeriod??9}/${cfg?.maCross?.slowPeriod??20}`,
      value: maCross.bullish?'↑ Bullish':'↓ Bearish',
      sub: maCross.bullishCross?'just crossed up':maCross.bearishCross?'just crossed down':'trending',
      color: maCross.bullish?'var(--up)':'var(--down)' },
    stoch && { label:'Stochastic %K', value: stoch.k.toFixed(0)+'%',
      sub: stoch.k>80?'overbought':stoch.k<20?'oversold':'neutral',
      color: stoch.k>80?'var(--down)':stoch.k<20?'var(--up)':'var(--muted)' },
    rsiVal != null && { label:'RSI', value: rsiVal.toFixed(0),
      sub: rsiVal>70?'overbought':rsiVal<30?'oversold':'neutral',
      color: rsiVal>70?'var(--down)':rsiVal<30?'var(--up)':'var(--muted)' },
  ].filter(Boolean);

  if (!items.length) {
    const needed = Math.max(0, 34 - minutesIn);
    return (
      <div style={{ fontSize:11, color:'var(--faint)', textAlign:'center', padding:'10px 0' }}>
        Indicators loading — need more price bars ({needed > 0 ? `~${needed} more min` : 'computing…'})
      </div>
    );
  }

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(120px, 1fr))', gap:8, marginBottom:12 }}>
      {items.map((item,i) => (
        <div key={i} style={{ padding:'8px 10px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg)' }}>
          <div style={{ fontSize:9, color:'var(--faint)', marginBottom:3, textTransform:'uppercase', letterSpacing:'.06em' }}>{item.label}</div>
          <div style={{ fontSize:14, fontWeight:800, fontFamily:'monospace', color:item.color }}>{item.value}</div>
          <div style={{ fontSize:9, color:'var(--faint)' }}>{item.sub}</div>
        </div>
      ))}
    </div>
  );
}

// Settings panel
function GameDaySettings({ settings, onSave }) {
  const [draft, setDraft]     = useState(() => mergeGameDefaults(settings));
  const [dirty, setDirty]     = useState(false);
  const [saving, setSaving]   = useState(false);
  const [ringtone, setRingtone] = useState({ url:null, name:'' });
  const upd = (k,v) => { setDraft(d=>({...d,[k]:v})); setDirty(true); };

  useEffect(()=>{ tierGetRingtone(GAME_RINGTONE_ID).then(url=>{ if(url) setRingtone({url,name:'Custom tone'}); }).catch(()=>{}); },[]);

  const testAudio = () => {
    if (ringtone.url) {
      const a=new Audio(ringtone.url);
      playAudio(a,{targetVol:draft.volume,cutoffSecs:draft.ringtoneCutoffSecs,gradual:draft.gradualVolume});
    } else { playBeep('bullish',draft.volume); }
  };

  const doSave = async () => {
    setSaving(true);
    try { await onSave(draft); setDirty(false); } catch(e){ alert('Save failed: '+e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ borderTop:'1px solid var(--border)', paddingTop:14, marginTop:6 }}>
      <div style={{ fontSize:11, fontWeight:800, letterSpacing:'.06em', color:'var(--text)', marginBottom:12 }}>GAME DAY SETTINGS</div>

      {/* Ticker */}
      <div style={{ padding:'12px 14px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg)', marginBottom:10 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', marginBottom:6, letterSpacing:'.06em', textTransform:'uppercase' }}>Ticker</div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <input type="text" value={draft.ticker||''} onChange={e=>upd('ticker',e.target.value.toUpperCase().trim())}
            placeholder="NVDA" maxLength={6}
            style={{ width:100, padding:'6px 10px', background:'var(--bg)', border:'1px solid var(--border)',
              borderRadius:5, color:'var(--text)', fontSize:14, fontWeight:800, fontFamily:"'IBM Plex Mono',monospace",
              textTransform:'uppercase' }}/>
          <span style={{ fontSize:11, color:'var(--faint)', lineHeight:1.5 }}>
            Add this ticker to your watched symbols in the main Settings so live intraday indicators are available.
          </span>
        </div>
      </div>

      {/* Master enable */}
      <div style={{ padding:'10px 14px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg)', marginBottom:10 }}>
        <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12 }}>
          <input type="checkbox" checked={draft.enabled} onChange={e=>upd('enabled',e.target.checked)}
            style={{ accentColor:'var(--accent)', width:14, height:14 }}/>
          <span style={{ color:'var(--text)', fontWeight:600 }}>Alerts enabled</span>
        </label>
      </div>

      {/* Volume & cooldown */}
      <div style={{ padding:'12px 14px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg)', marginBottom:10 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', marginBottom:10, letterSpacing:'.06em', textTransform:'uppercase' }}>Audio</div>
        <GdSlider label="Volume" value={Math.round(draft.volume*100)} unit="%" min={0} max={100} step={5} onChange={v=>upd('volume',v/100)}/>
        <GdSlider label="Cooldown between same alert" value={draft.cooldownMins} unit=" min" min={1} max={30} step={1} onChange={v=>upd('cooldownMins',v)}/>
        <div style={{ display:'flex', gap:6, marginTop:8 }}>
          <label style={{ ...fI, display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:11 }}>
            <input type="checkbox" checked={draft.audioEnabled} onChange={e=>upd('audioEnabled',e.target.checked)}/>Audio
          </label>
          {ringtone.url
            ? <div style={{ display:'flex', gap:6 }}>
                <span style={{ fontSize:10, color:'var(--up)', padding:'3px 8px', background:'var(--up-soft)', borderRadius:4 }}>🎵 {ringtone.name}</span>
                <button onClick={testAudio} style={gBtn}>▶ Test</button>
                <button onClick={async()=>{await tierDeleteRingtone(GAME_RINGTONE_ID);setRingtone({url:null,name:''}); }} style={gBtn}>Remove</button>
              </div>
            : <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                <label style={{ ...gBtn, cursor:'pointer' }}>
                  ↑ Upload ringtone
                  <input type="file" accept="audio/*" style={{ display:'none' }} onChange={async e=>{
                    const f=e.target.files[0]; if(!f) return;
                    const dr=await new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result);r.onerror=rej;r.readAsDataURL(f);});
                    await tierPutRingtone(GAME_RINGTONE_ID,dr);setRingtone({url:dr,name:f.name});e.target.value='';
                  }}/>
                </label>
                <button onClick={testAudio} style={gBtn}>▶ Test beep</button>
              </div>
          }
        </div>
      </div>

      {/* Alert types */}
      <div style={{ padding:'12px 14px', borderRadius:8, border:'1px solid var(--border)', background:'var(--bg)', marginBottom:10 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'var(--muted)', marginBottom:10, letterSpacing:'.06em', textTransform:'uppercase' }}>Alert Types</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
          {Object.entries(draft.alertTypes||{}).map(([key,t])=>(
            <label key={key} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer',
              padding:'6px 10px', borderRadius:6, border:`1px solid ${t.enabled?t.color+'50':'var(--border)'}`,
              background:t.enabled?t.color+'0c':'transparent' }}>
              <input type="checkbox" checked={t.enabled}
                onChange={e=>{setDraft(d=>({...d,alertTypes:{...d.alertTypes,[key]:{...t,enabled:e.target.checked}}}));setDirty(true);}}
                style={{ accentColor:t.color, flexShrink:0 }}/>
              <span style={{ fontSize:11, fontWeight:600, color:t.enabled?t.color:'var(--faint)' }}>{t.label}</span>
            </label>
          ))}
        </div>
      </div>

      {dirty && (
        <div style={{ position:'sticky', bottom:16, display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'10px 14px', borderRadius:6, border:'1px solid var(--border)', background:'var(--bg)', boxShadow:'0 4px 16px rgba(0,0,0,.3)' }}>
          <span style={{ fontSize:12, color:'var(--muted)' }}>Unsaved changes</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={()=>{setDraft(mergeGameDefaults(settings));setDirty(false);}} style={gBtn}>Discard</button>
            <button onClick={doSave} disabled={saving}
              style={{...gBtn, background:'var(--accent)', color:'var(--bg)', borderColor:'var(--accent)', fontWeight:700}}>
              {saving?'Saving…':'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function GdSlider({ label, value, unit='', min, max, step, onChange }) {
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--muted)', marginBottom:3 }}>
        <span>{label}</span>
        <span style={{ fontFamily:'monospace', fontWeight:700, color:'var(--text)' }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e=>onChange(Number(e.target.value))} style={{ width:'100%', accentColor:'var(--accent)' }}/>
    </div>
  );
}

function mergeGameDefaults(s) {
  const d = DEFAULT_GAME_DAY_ALERT;
  if (!s) return JSON.parse(JSON.stringify(d));
  return { ...d, ...s,
    alertTypes: Object.fromEntries(Object.entries(d.alertTypes).map(([k,def])=>[k,{...def,...(s.alertTypes?.[k]??{})}])),
    indicators: Object.fromEntries(Object.entries(d.indicators).map(([k,def])=>[k,{...def,...(s.indicators?.[k]??{})}])),
  };
}

const fL = { fontSize:9, color:'var(--faint)', marginBottom:3, textTransform:'uppercase', letterSpacing:'.06em' };
const fI = { padding:'6px 9px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:5, color:'var(--text)', fontSize:11, fontFamily:"'IBM Plex Mono',monospace" };
const gBtn = { fontSize:10, padding:'4px 10px', border:'1px solid var(--border)', borderRadius:4, background:'transparent', color:'var(--muted)', cursor:'pointer' };

// ── Root component ────────────────────────────────────────────────────────────
export default function GameDayTab({ engineOn, symbolStates, marketOpen, globalSettings, saveGlobalSettings, vixQuote }) {
  const alertCfg  = mergeGameDefaults(globalSettings?.gameDayAlerts);
  const ticker    = alertCfg.ticker || 'NVDA';
  const vixPrice  = vixQuote?.regularMarketPrice ?? vixQuote?.preMarketPrice ?? null;

  const [showSettings, setShowSettings] = useState(false);
  const [localPM, setLocalPM]       = useState(null);
  const [pmLoading, setPmLoading]   = useState(false);
  const [pmFetchedAt, setPmFetchedAt] = useState(null);
  const fetchedTickerRef = useRef(null);

  // News headlines state
  const [news, setNews]             = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const newsFetchedRef = useRef(null);

  // Bootstrap pre-market data — fetches on ticker change or manual refresh
  const fetchPM = useCallback((force = false) => {
    const staleMs = 60 * 60 * 1000; // 1 hour
    if (!force && fetchedTickerRef.current === ticker && pmFetchedAt && Date.now() - pmFetchedAt < staleMs) return;
    fetchedTickerRef.current = ticker;
    setPmLoading(true);
    const h = { 'x-session-token': getToken() };
    Promise.all([
      fetch(`${BASE}/chart?symbol=${encodeURIComponent(ticker)}&interval=1d&range=2mo`, {headers:h}).then(r=>r.ok?r.json():null),
      fetch(`${BASE}/quote?symbols=${encodeURIComponent(ticker)}`, {headers:h}).then(r=>r.ok?r.json():null),
    ]).then(([chartData, quoteData]) => {
      let bars = [];
      if (chartData?.bars?.length) bars = chartData.bars.filter(b=>b.c!=null).map(b=>({time:b.t/1000,open:b.o,high:b.h,low:b.l,close:b.c,volume:b.v??0}));
      const q = quoteData?.[ticker] ?? quoteData?.quoteResponse?.result?.[0] ?? null;
      const preMarketPrice = q?.preMarketPrice ?? q?.regularMarketPrice ?? null;
      setLocalPM(analyzePreMarket(bars, preMarketPrice));
      setPmFetchedAt(Date.now());
      setPmLoading(false);
    }).catch(()=>setPmLoading(false));
  }, [ticker, pmFetchedAt]);

  useEffect(() => { fetchPM(false); }, [ticker]); // eslint-disable-line

  // News headlines — refresh on ticker change or when stale (> 1h)
  const fetchNews = useCallback((force = false) => {
    if (!force && newsFetchedRef.current === ticker) return;
    newsFetchedRef.current = ticker;
    setNewsLoading(true);
    setNews(null);
    fetch(`${BASE}/news/headlines?symbol=${encodeURIComponent(ticker)}`, {
      headers: { 'x-session-token': getToken() },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setNews(d); })
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, [ticker]);

  useEffect(() => { fetchNews(false); }, [ticker]); // eslint-disable-line
  const gameSt   = symbolStates?.[ticker] ?? null;
  const bars      = gameSt?.bars ?? [];
  const minutesIn = gameSt?.minutesIntoSession ?? 0;
  const price     = gameSt?.price;
  const vwap      = gameSt?.vwap;
  const atrPct    = gameSt?.atrPct;
  const pm        = localPM;

  // Compute indicators
  const indCfg   = alertCfg.indicators ?? {};
  const macdCfg  = indCfg.macd ?? {};
  const maCfg    = indCfg.maCross ?? {};
  const stochCfg = indCfg.stochastic ?? {};
  const rsiCfg   = indCfg.rsiExit ?? {};

  const barsForMACD  = aggregateBars(bars, macdCfg.timeframeMins??1);
  const barsForMA    = aggregateBars(bars, maCfg.timeframeMins??5);
  const barsForStoch = aggregateBars(bars, stochCfg.timeframeMins??1);
  const barsForRSI   = aggregateBars(bars, rsiCfg.timeframeMins??1);

  const macd    = calcMACD(barsForMACD.map(b=>b.close), macdCfg.fast??12, macdCfg.slow??26, macdCfg.signal??9);
  const maCross = calcMACross(barsForMA.map(b=>b.close), maCfg.fastPeriod??9, maCfg.slowPeriod??20);
  const stoch   = calcStochastic(barsForStoch, stochCfg.kPeriod??14, stochCfg.dPeriod??3);
  const rsi     = calcRSI(barsForRSI.map(b=>b.close), rsiCfg.period??14);

  const vwapCrossed = (() => {
    if (!vwap||bars.length<2) return null;
    const band=vwap*((indCfg.vwapCross?.bandPct??0.05)/100);
    const cur=bars[bars.length-1]?.close, prv=bars[bars.length-2]?.close;
    if (!cur||!prv) return null;
    if (prv>vwap+band&&cur<vwap-band) return 'below';
    if (prv<vwap-band&&cur>vwap+band) return 'above';
    return null;
  })();

  const allInds = { macd, maCross, stoch, rsi, vwapCrossed };

  // ORB and EMA direction — computed first so direction panel can use live signals
  const orbBars = bars.slice(0, 15);
  const orbHigh = orbBars.length ? Math.max(...orbBars.map(b=>b.high)) : null;
  const orbLow  = orbBars.length ? Math.min(...orbBars.map(b=>b.low))  : null;
  const orbDir  = minutesIn >= 15 && orbHigh && orbLow
    ? (price > orbHigh + orbHigh*0.0005 ? 'up' : price < orbLow - orbLow*0.0005 ? 'down' : null) : null;

  const candles5   = aggregateBars(bars, 5);
  const closes5    = candles5.map(c=>c.close);
  const ema9on5    = calcEMA(closes5, 9);
  const last2      = candles5.slice(-2);
  const emaDir     = ema9on5 && last2.length===2 && vwap
    ? (last2.every(c=>c.close>vwap&&c.close>ema9on5)?'up'
     : last2.every(c=>c.close<vwap&&c.close<ema9on5)?'down':null) : null;

  // Opening direction — uses live bar signals first, falls back to pre-market
  const direction = getOpeningDirection(pm, vixPrice, orbDir, emaDir);

  // Suggested spread type for position form
  const w = 4; // default spread width for game day
  const suggestedType = orbDir==='up'||emaDir==='up' ? 'call_debit'
                      : orbDir==='down'||emaDir==='down' ? 'put_debit' : '';
  const suggestedSell = suggestedType&&price ? Math.ceil(price) : null;
  const suggestedBuy  = suggestedSell ? (suggestedType==='call_debit'?suggestedSell+w:suggestedSell-w) : null;

  // Position state
  const posKey = `gd:position:${ticker}`;
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem(posKey)||'null') ?? { type:'', longStrike:'', shortStrike:'', premium:'', contracts:1, active:false }; }
    catch { return { type:'', longStrike:'', shortStrike:'', premium:'', contracts:1, active:false }; }
  });
  const handlePosChange = (next) => { setPos(next); localStorage.setItem(posKey, JSON.stringify(next)); };

  const exitSignals = calcExitSignals(pos, price, allInds, vwap, atrPct, alertCfg, ticker);

  // Alerts
  const notifiedRef = useRef(new Set());
  const lastFireRef = useRef({});
  const ringtoneRef = useRef(null);
  useEffect(()=>{ tierGetRingtone(GAME_RINGTONE_ID).then(url=>{ if(url) ringtoneRef.current=new Audio(url); }).catch(()=>{}); },[]);

  useEffect(()=>{
    if (!alertCfg.enabled||!pos.active||!exitSignals.length) return;
    const nowMs=Date.now(), coolMs=(alertCfg.cooldownMins??3)*60_000;
    for (const sig of exitSignals) {
      const typeKey=sig.level.replace(/ /g,'_');
      const typeCfg=alertCfg.alertTypes[typeKey];
      if (!typeCfg?.enabled) continue;
      const dk=`${typeKey}:${sig.text.slice(0,50)}`;
      if (nowMs-(lastFireRef.current[typeKey]??0)<coolMs) continue;
      if (notifiedRef.current.has(dk)) continue;
      notifiedRef.current.add(dk); lastFireRef.current[typeKey]=nowMs;
      if (alertCfg.audioEnabled) {
        if (ringtoneRef.current) playAudio(ringtoneRef.current,{targetVol:alertCfg.volume,cutoffSecs:alertCfg.ringtoneCutoffSecs,gradual:alertCfg.gradualVolume});
        else playBeep(typeCfg.severity,alertCfg.volume);
      }
      if (alertCfg.browserNotif&&Notification?.permission==='granted') {
        new Notification(`${ticker} Game Day — ${sig.level}`,{body:sig.text,icon:'/favicon.ico',tag:dk});
      }
    }
  }, [exitSignals.map(s=>s.level+s.text.slice(0,20)).join('|'), alertCfg.enabled, pos.active]);

  useEffect(()=>{ notifiedRef.current=new Set(); lastFireRef.current={}; },[marketOpen]);

  const saveGameSettings = useCallback(async (draft) => {
    await saveGlobalSettings?.({...globalSettings, gameDayAlerts: draft});
  }, [globalSettings, saveGlobalSettings]);

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 4px', marginBottom:8, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:8 }}>
          <span style={{ fontFamily:'monospace', fontWeight:900, fontSize:16, color:'var(--text)' }}>{ticker}</span>
          {price!=null && <span style={{ fontFamily:'monospace', fontSize:13, color:'var(--faint)' }}>${price.toFixed(2)}</span>}
          {vwap   && <span style={{ fontSize:10, color:'var(--warn)',  fontFamily:'monospace' }}>VWAP ${vwap.toFixed(2)}</span>}
          {minutesIn>0 && <span style={{ fontSize:10, color:'var(--faint)', fontFamily:'monospace' }}>{minutesIn}min in</span>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:alertCfg.enabled?'var(--up)':'var(--faint)' }}>
            <span style={{ width:7, height:7, borderRadius:'50%', background:alertCfg.enabled?'var(--up)':'var(--faint)', display:'inline-block' }}/>
            {alertCfg.enabled?'Alerts on':'Alerts off'}
          </span>
          {!gameSt && <span style={{ fontSize:9, color:'var(--warn)', padding:'2px 7px', border:'1px solid var(--warn)', borderRadius:3 }}>Add {ticker} to watched symbols for live indicators</span>}
          <button onClick={()=>setShowSettings(s=>!s)}
            style={{ fontSize:10, padding:'3px 9px', border:'1px solid var(--border)', borderRadius:4,
              background:showSettings?'var(--border)':'transparent', color:'var(--muted)', cursor:'pointer' }}>
            ⚙ {showSettings?'Hide':'Settings'}
          </button>
        </div>
      </div>

      {showSettings && <GameDaySettings settings={globalSettings?.gameDayAlerts} onSave={saveGameSettings}/>}

      {/* Pre-market direction + refresh */}
      {pmLoading
        ? <div style={{ fontSize:11, color:'var(--faint)', padding:'10px 0' }}>⟳ Loading pre-market data for {ticker}…</div>
        : <>
            <DirectionPanel direction={direction} marketOpen={marketOpen} minutesIn={minutesIn} ticker={ticker}/>
            {pmFetchedAt && (
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:-8, marginBottom:6 }}>
                <button onClick={()=>{ fetchPM(true); fetchNews(true); }}
                  style={{ fontSize:9, padding:'2px 8px', border:'1px solid var(--border)', borderRadius:3,
                    background:'transparent', color:'var(--faint)', cursor:'pointer' }}>
                  ↺ Refresh direction &amp; news
                </button>
              </div>
            )}
          </>
      }

      {/* News — pre-market and first 15 min after open */}
      {(!marketOpen || minutesIn < 15) && (
        <NewsPanel news={news} loading={newsLoading} ticker={ticker} />
      )}

      {/* Strategy A & B (same as Safe Day but for game day ticker) */}
      {marketOpen && bars.length > 0 && (
        <>
          <CombinedSignal orbDir={orbDir} emaDir={emaDir} />
          {pos.active && exitSignals.length > 0 && <ExitSignalSummary signals={exitSignals} pos={pos} />}
          <StrategyA bars={bars} minutesIn={minutesIn} price={price} atrPct={atrPct} ticker={ticker} />
          <StrategyB bars={bars} minutesIn={minutesIn} price={price} vwap={vwap} atrPct={atrPct} ticker={ticker} />
        </>
      )}

      {/* Live P&L bar */}
      {pos.active && price && <PnlBar pos={pos} currentPrice={price}/>}

      {/* Exit plan — same layout as Safe Day */}
      {/* GEX levels */}
      <GEXPanel price={price} vwap={vwap} atrPct={atrPct} ticker={ticker} bars={bars} pm={pm} marketOpen={marketOpen} />

      {/* 0DTE flow */}
      <ZeroDTEFlowPanel ticker={ticker} flowKey={`gd:0dte:${ticker}`} />

      <div style={{ margin:'4px 0 8px', borderTop:'1px solid var(--border)', paddingTop:14 }}>
        <div style={{ fontSize:11, fontWeight:800, letterSpacing:'.06em', color:'var(--text)', marginBottom:12 }}>
          EXIT PLAN — {ticker}
        </div>

        {/* IBKR-style options chain picker */}
        <OptionsChainPicker price={price} ticker={ticker} pos={pos} onChange={handlePosChange}
          expiryLabel="Today (0DTE)" />

        {pos.active && !exitSignals.length && (
          <div style={{ padding:'10px 16px', borderRadius:8, border:'1px solid var(--up)', background:'#22c55e0e', marginBottom:14 }}>
            <span style={{ fontSize:12, fontWeight:700, color:'var(--up)' }}>✓ Position looks fine</span>
            <span style={{ fontSize:11, color:'var(--muted)', marginLeft:10 }}>No exit signals. Continue monitoring.</span>
          </div>
        )}
        {marketOpen && bars.length > 0 && (
          <>
            <MACrossPanel maCross={maCross} posType={pos.active ? pos.type : ''} />
            <MACDPanel    macd={macd}       posType={pos.active ? pos.type : ''} />
          </>
        )}
      </div>

      <div style={{ fontSize:9, color:'var(--faint)', textAlign:'center', padding:'8px 0', lineHeight:1.6 }}>
        P&L is an intrinsic value estimate only — actual option value depends on time, IV, and bid-ask. Always verify with your broker. Not investment advice.
      </div>
    </div>
  );
}
